// Column order of the digest email, one block per template.
//
// The schedule stores ONE ordered column list (form.columns): the standard columns (builtin)
// and every template's question columns. The server renders each template block with the
// standard columns + that template's own fields, in that one order. So each template's own
// sequence (standard columns + its fields) can be arranged freely, as long as the standard
// columns keep the same relative order in every block - which is what mergeColumns keeps.

const LEGACY_GROUP = ''

/** Template key of a question column: the numeric template Id as a string ('' = older row without one). */
export const groupKeyOf = (column, templateRecords = []) => {
    if (!column || column.builtin) return null
    const raw = column.templateId
    if (raw == null || raw === '') return LEGACY_GROUP
    const asText = String(raw)
    const byGuid = (templateRecords || []).find(
        record => record?.TemplateId != null && String(record.TemplateId) === asText,
    )
    return byGuid ? String(byGuid.Id) : asText
}

/** Standard columns + the fields of one template, in email order. */
export const templateSequence = (columns, templateKey, templateRecords) =>
    (columns || []).filter(
        column =>
            column.builtin ||
            groupKeyOf(column, templateRecords) === String(templateKey),
    )

export const fieldsOf = (columns, templateKey, templateRecords) =>
    templateSequence(columns, templateKey, templateRecords).filter(
        column => !column.builtin,
    )

const groupKeys = (columns, groupOrder, templateRecords) => {
    const keys = (groupOrder || []).map(String)
    ;(columns || []).forEach(column => {
        const key = groupKeyOf(column, templateRecords)
        if (key != null && !keys.includes(key)) keys.push(key)
    })
    return keys
}

/**
 * Rebuild the one column list after a template's sequence changed.
 * override = { templateKey, sequence } (sequence = standard columns + that template's fields).
 * The standard columns take the override's order everywhere; each other template keeps every
 * field in the same "slot" (number of standard columns before it), so nobody else's order moves.
 */
export const mergeColumns = (columns, groupOrder, templateRecords, override) => {
    const list = columns || []
    const currentBuiltins = list.filter(column => column.builtin)
    let builtins = currentBuiltins
    if (override) {
        const fromOverride = override.sequence.filter(column => column.builtin)
        const seen = new Set(fromOverride.map(column => column.key))
        builtins = [
            ...fromOverride,
            ...currentBuiltins.filter(column => !seen.has(column.key)),
        ]
    }

    const slots = new Map()
    groupKeys(list, groupOrder, templateRecords).forEach(key => {
        const sequence =
            override && String(override.templateKey) === key
                ? override.sequence
                : templateSequence(list, key, templateRecords)
        let slot = 0
        const perSlot = []
        sequence.forEach(column => {
            if (column.builtin) {
                slot += 1
                return
            }
            if (!perSlot[slot]) perSlot[slot] = []
            perSlot[slot].push(column)
        })
        slots.set(key, perSlot)
    })

    const result = []
    for (let k = 0; k <= builtins.length; k += 1) {
        slots.forEach(perSlot => {
            const inSlot = perSlot[k] || []
            inSlot.forEach(column => result.push(column))
        })
        if (k < builtins.length) result.push(builtins[k])
    }
    return result
}

const builtinOrderKey = columns =>
    (columns || [])
        .filter(column => column.builtin)
        .map(column => column.key)
        .join(',')

/** True when the change moved standard columns (so every block changed). */
export const standardOrderChanged = (before, after) =>
    builtinOrderKey(before) !== builtinOrderKey(after)

/** Shown in the block: visible standard columns and every field. */
export const isShownInBlock = column =>
    !column.builtin || column.locked || column.visible

/** Move one column of a template to before (or after) another column of the same template. */
export const moveInTemplate = (
    columns,
    groupOrder,
    templateRecords,
    templateKey,
    key,
    targetKey,
    after = false,
) => {
    const sequence = templateSequence(columns, templateKey, templateRecords)
    const from = sequence.findIndex(column => column.key === key)
    if (from < 0 || key === targetKey) return columns
    const next = [...sequence]
    const [moved] = next.splice(from, 1)
    const target = next.findIndex(column => column.key === targetKey)
    if (target < 0) return columns
    next.splice(after ? target + 1 : target, 0, moved)
    return mergeColumns(columns, groupOrder, templateRecords, {
        templateKey,
        sequence: next,
    })
}

/** Move a column one place left (-1) or right (+1) among the columns shown in the block. */
export const stepInTemplate = (
    columns,
    groupOrder,
    templateRecords,
    templateKey,
    key,
    direction,
) => {
    const shown = templateSequence(columns, templateKey, templateRecords).filter(
        isShownInBlock,
    )
    const index = shown.findIndex(column => column.key === key)
    const target = shown[index + direction]
    if (index < 0 || !target) return columns
    return moveInTemplate(
        columns,
        groupOrder,
        templateRecords,
        templateKey,
        key,
        target.key,
        direction > 0,
    )
}

/** Add fields at the end of a template's block. */
export const appendFields = (
    columns,
    groupOrder,
    templateRecords,
    templateKey,
    newColumns,
) => {
    const sequence = templateSequence(columns, templateKey, templateRecords)
    const present = new Set((columns || []).map(column => column.key))
    const added = (newColumns || []).filter(column => !present.has(column.key))
    if (!added.length) return columns
    return mergeColumns(columns, groupOrder, templateRecords, {
        templateKey,
        sequence: [...sequence, ...added],
    })
}

export const removeColumnByKey = (columns, key) =>
    (columns || []).filter(column => column.builtin || column.key !== key)

export const removeTemplateFields = (columns, templateKey, templateRecords) =>
    (columns || []).filter(
        column =>
            column.builtin ||
            groupKeyOf(column, templateRecords) !== String(templateKey),
    )

/** Standard columns + the fields of the given templates (drops fields of templates not on the schedule). */
export const keepChosenTemplateFields = (columns, templateIds, templateRecords) => {
    const chosen = new Set((templateIds || []).map(String))
    return (columns || []).filter(column => {
        if (column.builtin) return true
        return chosen.has(groupKeyOf(column, templateRecords))
    })
}

export const normalizeText = value =>
    String(value || '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim()

/**
 * Give the other templates the same order as one template: standard columns in that order,
 * fields with the same question text in the same places; fields they don't share go last.
 */
export const copyOrderToOthers = (
    columns,
    groupOrder,
    templateRecords,
    sourceKey,
    otherKeys,
) => {
    const source = templateSequence(columns, sourceKey, templateRecords)
    let next = columns
    ;(otherKeys || []).forEach(key => {
        if (String(key) === String(sourceKey)) return
        const mine = fieldsOf(next, key, templateRecords)
        const sequence = []
        source.forEach(column => {
            if (column.builtin) {
                sequence.push(
                    next.find(item => item.builtin && item.key === column.key) ||
                        column,
                )
                return
            }
            const text = normalizeText(column.questionText)
            const match = mine.find(item => normalizeText(item.questionText) === text)
            if (match) {
                sequence.push(match)
                mine.splice(mine.indexOf(match), 1)
            }
        })
        next = mergeColumns(next, groupOrder, templateRecords, {
            templateKey: key,
            sequence: [...sequence, ...mine],
        })
    })
    return next
}

/**
 * Questions whose text appears in two or more of the given templates.
 * questionsByTemplate = { [templateKey]: [{ id, text, ... }] } (only loaded templates count).
 */
export const sharedQuestions = (templateKeys, questionsByTemplate) => {
    const map = new Map()
    ;(templateKeys || []).forEach(key => {
        const list = questionsByTemplate?.[String(key)]
        if (!Array.isArray(list)) return
        list.forEach(question => {
            const text = normalizeText(question.text)
            if (!text) return
            if (!map.has(text)) map.set(text, { text: question.text, perTemplate: {} })
            const entry = map.get(text)
            if (!entry.perTemplate[String(key)]) entry.perTemplate[String(key)] = question
        })
    })
    return [...map.values()].filter(entry => Object.keys(entry.perTemplate).length > 1)
}
